import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { CancellationBlockedReason } from '@acorn-portal/shared/src/pages/policy/types';
import { CancellationIneligibilityReason } from '@acorn-portal/shared/src/services/cancellation-api';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';
import { Alert } from '@mui/material';
import ClaimNotificationContent from './notification-content/claim-notification-content';
import PaymentNotificationContent from './notification-content/payment-notification-content';
import RenewalPeriodContent from './notification-content/renewal-period-notification-content';
import ContactDetailsContent from './notification-content/contact-details-content';

interface ICancellationAccessNotification {
	reasonBlocked: CancellationBlockedReason;
}

/**
 * Helper component to manage Cancellation access notifications
 * @param reasonBlocked - the reason why Cancellation access is blocked, used to determine which notification to show
 * @remarks The contact number to display in the notification is worked out by processing the state value for `fullPolicy.product` with `getPhoneNumber`.
 */
const CancellationAccessNotification = ({ reasonBlocked }: ICancellationAccessNotification) => {
	const product = useAppSelector((state) => state.fullPolicy.product);
	const contactNumber = getPhoneNumber(product, 'CONTACT');

	if (!reasonBlocked) return null;

	const renderContent = () => {
		switch (reasonBlocked) {
			case CancellationIneligibilityReason.PolicyHasOpenClaim:
			case CancellationIneligibilityReason.PolicyHasFaultClaim:
				// Claim notification content
				return <ClaimNotificationContent contactNumber={contactNumber} />;
			case CancellationIneligibilityReason.PaymentRequested:
				// Payment notification content
				return <PaymentNotificationContent contactNumber={contactNumber} />;
			case CancellationIneligibilityReason.PolicyInRenewalWindow:
				// Renewal period notification content
				return <RenewalPeriodContent contactNumber={contactNumber} />;
			// Add other specific reasons here as needed
			default:
				// Fallback content
				return <ContactDetailsContent contactNumber={contactNumber} />;
		}
	};

	return (
		<Alert variant='outlined' severity='info' sx={{ fontSize: '14px', a: { color: 'inherit' } }}>
			{renderContent()}
		</Alert>
	);
};

export default CancellationAccessNotification;
